/**
 * NameAndSubmitCard — final review + batch name edit + two toggles
 * (auto-redeem, allow-overwrite-duplicates) + "Send it" button.
 *
 */

import { useState } from 'react'
import { Send } from 'lucide-react'
import { AgentBubble } from './AgentBubble'

interface NameAndSubmitCardProps {
  barcodeCount: number
  eventName: string
  planName: string
  defaultName: string
  busy: boolean
  onSubmit: (body: {
    name: string
    isAutoRedeemed: boolean
    allowOverwriteForDuplicate: boolean
  }) => void
}

export function NameAndSubmitCard({
  barcodeCount,
  eventName,
  planName,
  defaultName,
  busy,
  onSubmit,
}: NameAndSubmitCardProps) {
  const [name, setName] = useState(defaultName)
  const [autoRedeem, setAutoRedeem] = useState(false)
  // Austin-file-splitter ships with overwrite=true by default. Keep parity.
  const [allowOverwrite, setAllowOverwrite] = useState(true)

  return (
    <div className="w-full max-w-xl space-y-4">
      <AgentBubble from="agent">
        Ready. <strong>{barcodeCount.toLocaleString()}</strong> barcodes, event{' '}
        <strong>{eventName}</strong>, plan <strong>{planName}</strong>. I'll
        name the batch below — edit if you want.
      </AgentBubble>

      <div className="space-y-3 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-700 dark:bg-slate-800">
        <label className="block">
          <span className="text-xs font-medium text-slate-600 dark:text-slate-300">
            Batch name
          </span>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-carnival-400 dark:border-slate-600 dark:bg-slate-700 dark:text-white"
          />
        </label>

        <label className="flex items-center justify-between gap-3 pt-1">
          <span className="text-xs text-slate-700 dark:text-slate-200">
            Auto-redeem barcodes
          </span>
          <input
            type="checkbox"
            checked={autoRedeem}
            onChange={(e) => setAutoRedeem(e.target.checked)}
            className="h-4 w-4 rounded border-slate-300 text-carnival-400 focus:ring-carnival-400"
          />
        </label>

        <label className="flex items-center justify-between gap-3">
          <span className="text-xs text-slate-700 dark:text-slate-200">
            Allow overwrite for duplicates
          </span>
          <input
            type="checkbox"
            checked={allowOverwrite}
            onChange={(e) => setAllowOverwrite(e.target.checked)}
            className="h-4 w-4 rounded border-slate-300 text-carnival-400 focus:ring-carnival-400"
          />
        </label>
      </div>

      <button
        type="button"
        disabled={busy || !name.trim()}
        onClick={() =>
          onSubmit({
            name: name.trim(),
            isAutoRedeemed: autoRedeem,
            allowOverwriteForDuplicate: allowOverwrite,
          })
        }
        className="inline-flex items-center gap-1.5 rounded-lg bg-carnival-400 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-carnival-500 disabled:opacity-50"
      >
        <Send className="h-4 w-4" />
        {busy ? 'Sending…' : 'Send it'}
      </button>
    </div>
  )
}
